import { useEffect, useState, useContext, useRef } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import UserDashboard from "./components/dashboard";
import { LoginService, OpenAPI } from "@propertelligent/client-api";
import { ForgetPassword } from "./components/user/ForgetPassword";
import CheckEmail from "./components/user/CheckEmail";
import { GeneratePdfDocument } from "./components/generatePdfDocument";
import ResetPassword from "./components/user/ResetPassword";
import { MyContext } from "./utils/context/LoginContext";
import { UserLoginData } from "./components/common/charts/Enums";
import { Login } from "./components/user/Login";
import Signup from "./components/user/Signup";
import { MAIN_ROUTES_URLS, URLS, USER_ROLES, USERS } from "./constants/constants";
import { useDispatch } from "react-redux";
import GmailAuthReturn from "./components/Messaging/Email/components/GmailAuthRetrun";
import NewQuestionnaire from "./components/user/NewQuestionnaire";
import CustomMenu from "./layout/CustomMenu";
import CustomTopBar from "./layout/CustomTopBar";
import { MenuContext } from "./utils/context/MenuContext";
import { NotificationPayload, NotificationType, PubSub, TopicsEnum } from "./utils/pubsub";
import Notification from "./components/common/Notification/Notification";
import { GenerateEmail } from "./components/generateEmail";
import { MyOrganization } from "./components/my-organization";
import TenantOnboard from "./components/TenantOnboard";
import InviteUser from "./components/user/inviteUser";
import { PlatformSettings } from "./components/platform-settings";
import ServerErrorPopup from "./newComponents/errorPopup/server-error-popup";
import Dashboard from "./tenantComponent/components/tenantDashboard";
import { SetPassword } from "./components/user/SetPassword";

export default function App(): JSX.Element | any {
  const [showNotification, setShowNotification] = useState(false);
  const [isGenerateView, setIsGenerateView] = useState(false);
  const [notificationPayload, setNotificationPayload] = useState<NotificationPayload>({ type: NotificationType.None, message: "", title: "", icon: undefined });
  const [showServerError, setShowServerError] = useState(false);
  const { isLogin, setIsLogin, userRole } = useContext(MyContext);
  const { isOpen, setIsOpen, isHidden, setIsHidden } = useContext(MenuContext);
  const location = useLocation();
  const userRoleId = sessionStorage.getItem("userRoleId");
  const userOnboarded = sessionStorage.getItem("userOnboarded");
  const tenantData = JSON.parse(sessionStorage.getItem("tenantInfo"));
  
  const size = window.innerWidth;
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const loginToken = sessionStorage.getItem(UserLoginData.token);

  // Scroll to top whenever route changes
useEffect(() => {
  // Scroll window
  window.scrollTo(0, 0);

  // Scroll all scrollable elements
  const allScrollable = document.querySelectorAll('*');

  allScrollable.forEach((el) => {
    if (el.scrollTop) {
      el.scrollTop = 0;
    }
  });
}, [location.pathname]);


  const triggerNotification = (data: NotificationPayload) => {
    setNotificationPayload(data);
    setShowNotification(true);
    window.setTimeout(() => {
      setShowNotification(false);
    }, 4000);
  };

  useEffect(() => {
    const currentLink = window.location.href;

    if (currentLink.includes("generatePdfDocument") || currentLink.includes("generateEmail")) {
      setIsGenerateView(true);
    }

    PubSub.subscribe(
      TopicsEnum.Notification.toString(),
      (data: NotificationPayload) => {
        triggerNotification(data);
      }
    );

    PubSub.subscribe(
      TopicsEnum.Logout.toString(),
      (data: NotificationPayload) => {
        LoginService.putApiLoginLogout();
        triggerNotification(data);
        sessionStorage.clear();
        setIsLogin(false);
        setTimeout(() => {
          navigate(URLS.LOGIN, {
            state: { isComingFromLogout: true },
          });
          navigate(URLS.HOME);
        }, 5000);
      }
    );

    PubSub.subscribe(
      TopicsEnum.ServerError.toString(),
      (data: NotificationPayload) => {
        setShowServerError(true);
      }
    );
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsHidden(window.innerWidth < 400);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [setIsHidden]);

  useEffect(() => {
    setIsLogin(loginToken);
    OpenAPI.TOKEN = loginToken;
  }, [loginToken, setIsLogin]);

  useEffect(() => {
    if (location.pathname === MAIN_ROUTES_URLS.ORGANIZATION || location.pathname === MAIN_ROUTES_URLS.PLATFORMSETTINGS) {
      setIsHidden(true);
    } else {
      setIsHidden(size < 400);
    }
  }, [location.pathname, setIsHidden, size]);

  const cleanPathname = location.pathname.replace(/^\/+|\/+$/g, "");
  const isOrganizationPath = cleanPathname.startsWith("organization");
  const isPlatformSettingsPath = cleanPathname.startsWith("platform-settings");
  const generatePdf = cleanPathname.startsWith("generatePdfDocument");
  return (
    <div className="App">
      {isLogin && isLogin ? (
        <>
          {USERS.USER === USER_ROLES[userRoleId] && (
            <>
              {generatePdf ? (
                <Routes>
                  <Route path={URLS.GENERATE_PDF_DOCUMENT} element={<GeneratePdfDocument />} />
                </Routes>
              ) : (
                <>
                  {(userOnboarded === "true") && (<CustomTopBar size={size} userRoleId={userRoleId} showUserName={true} />)}
                  <div className="menuBarContainer">
                    {userOnboarded === "true" && (
                      <div ref={menuRef}
                        className={isHidden ? "hiddenMenu" : isOpen
                          ? (isOrganizationPath || isPlatformSettingsPath)
                            ? "openedMenuOrganization"
                            : "openedMenu"
                          : "closedMenu"
                        } >
                        {!isOrganizationPath ? (
                          !isPlatformSettingsPath &&
                          <CustomMenu size={size} userRoleId={parseInt(userRoleId)} />
                        ) : (<MyOrganization />)}
                        {isPlatformSettingsPath && <PlatformSettings />}
                      </div>
                    )}
                    <div className={`${userOnboarded === "false" ? "hiddenMenuRightSide" : isOpen
                      ? (isOrganizationPath || isPlatformSettingsPath)
                        ? "openedMenuFullWidth"
                        : "openedMenuRightSide"
                      : "closeMenuRightSide"
                      }`}
                    >
                      <div className="app-container">
                        <Routes>
                          <Route path={URLS.USER_DASHBOARD} element={<UserDashboard userRoleId={parseInt(userRoleId)} />} />
                          <Route path="/callback/gmail" element={<GmailAuthReturn />} />
                          <Route path={MAIN_ROUTES_URLS.HOME} element={userOnboarded === "false" ? <NewQuestionnaire /> :
                            <UserDashboard userRoleId={parseInt(userRoleId)} />} />
                          <Route path={URLS.QUESTIONNAIRE} element={<NewQuestionnaire />} />
                          {isOrganizationPath && (
                            <Route path={MAIN_ROUTES_URLS.ORGANIZATION} element={<MyOrganization />} />
                          )}
                          {isPlatformSettingsPath && (
                            <Route path={MAIN_ROUTES_URLS.PLATFORMSETTINGS} element={<PlatformSettings />} />
                          )}
                        </Routes>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          {USERS.TENANT === USER_ROLES[userRoleId] && (
            <>
              {!tenantData?.unitId ? (
                <Routes>
                  <Route path={MAIN_ROUTES_URLS.HOME} element={<TenantOnboard userRoleId={parseInt(userRoleId)} />} />
                </Routes>
              ) : (
                <div className="menuBarContainer" style={{ display: "flex" }}>
                  <div className={isOpen ? "openedMenuRightSide" : "closeMenuRightSide"} >
                    <div className="app-container">
                      <Routes>
                        <Route path={MAIN_ROUTES_URLS.HOME} element={<Dashboard />} />
                      </Routes>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <Routes>
          <Route path={MAIN_ROUTES_URLS.HOME} element={<Login />} />
          <Route path={MAIN_ROUTES_URLS.TENANT_ONBOARD} element={<TenantOnboard />} />
          <Route path={MAIN_ROUTES_URLS.INVITE_USER} element={<InviteUser />} />
          <Route path={URLS.RESET_PASSWORD_BY_USERKEY} element={<ResetPassword />} />
          <Route path={URLS.FORGET_PASSWORD} element={<ForgetPassword />} />
          <Route path={URLS.CHECK_MAIL} element={<CheckEmail />} />
          <Route path={URLS.SIGN_UP} element={<Signup />} />
          <Route path={URLS.SET_PASSWORD} element={<SetPassword />} />
          <Route path={URLS.GENERATE_EMAIL} element={<GenerateEmail />} />
        </Routes>
      )}
      {showNotification && (
        <Notification
          payload={notificationPayload}
          setNotification={(value) => {
            setShowNotification(value);
          }}
        />
      )}
      <ServerErrorPopup
        isOpen={showServerError}
        onClose={() => setShowServerError(false)}
      />
    </div>
  );
}